import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const UserContext = createContext({
  user: null,
  setUser: () => {},
  refreshFromStorage: () => {},
});

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("userMedical");
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed?.data || null;
    } catch {
      return null;
    }
  });

  const refreshFromStorage = () => {
    try {
      const stored = localStorage.getItem("userMedical");
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (parsed?.data) {
        setUser(parsed.data);
      }
    } catch (e) {
      console.error("Failed to refresh user from storage:", e);
    }
  };

  // Keep localStorage in sync when `user` changes and broadcast an update
  useEffect(() => {
    try {
      if (user == null) return;
      const storedRaw = localStorage.getItem("userMedical");
      const stored = storedRaw ? JSON.parse(storedRaw) : { data: {} };
      const storedData = stored.data || {};
      const incoming = user || {};

      // Preserve existing stored photo unless a new STRING url/path arrives.
      const storedPhoto = storedData.personalPhoto;
      const incomingPhoto = incoming.personalPhoto;
      const useIncomingPhoto = typeof incomingPhoto === "string" && incomingPhoto.trim() !== "";
      const finalPhoto = useIncomingPhoto ? incomingPhoto : storedPhoto;

      const merged = {
        ...stored,
        data: {
          ...storedData,
          ...incoming,
          personalPhoto: finalPhoto,
        },
      };

      localStorage.setItem("userMedical", JSON.stringify(merged));

      // Mirror convenience fields if present
      if (merged.data?.fullName) {
        localStorage.setItem("userName", merged.data.fullName);
        localStorage.setItem("fullName", merged.data.fullName);
      }

      if (typeof finalPhoto === "string" && finalPhoto.trim() !== "") {
        localStorage.setItem("userPersonalPhoto", finalPhoto);
        localStorage.setItem("profilePhotoBust", String(Date.now()));
      }
      // Let the rest of the app know
      window.dispatchEvent(new CustomEvent("profile-updated"));
    } catch (e) {
      console.error("Failed to sync user to storage:", e);
    }
  }, [user]);

  useEffect(() => {
   
    const handleStorage = (e) => {
      if (e.key === "userMedical") refreshFromStorage();
    };

    const handleProfileUpdated = () => {
      refreshFromStorage();
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("profile-updated", handleProfileUpdated);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("profile-updated", handleProfileUpdated);
    };
  }, []);

  const value = useMemo(() => ({ user, setUser, refreshFromStorage }), [user]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
